import { useCallback } from 'react';

import { exists } from 'common/functions';
import { LogEntry, Query } from 'common/graphqlTypes';

import { DataRow, FieldRenderer } from 'plugins/types';
import { DATA_SOURCE_NAME_FIELD, Schema } from 'schemas';

import { TruncateWithTooltip } from '../components';
import {
  DataSourceFieldRenderer,
  DataSourceFieldRendererSize,
  EventTypeFieldRenderer,
  EventTypeFieldRendererSize,
} from '../field-renderers';
import { plugin } from '../plugin';

import './DVLogDetailsSummary.scss';

type FieldProps = {
  fieldId: string;
  label: string;
  renderer?: FieldRenderer;
  row: DataRow;
  schema?: Schema;
  value: string;
};

const Field = ({ fieldId, label, renderer: Renderer, row, schema, value }: FieldProps) => {
  const valueNode = Renderer ? (
    <Renderer fieldId={fieldId} row={row} schemaFields={schema?.fields} value={value} />
  ) : (
    <TruncateWithTooltip id={`DVLogDetailsSummary__Field__Value--${value}`} text={value} />
  );

  return (
    <div className="DVLogDetailsSummary__Field">
      <div className="DVLogDetailsSummary__Field__Label">
        <TruncateWithTooltip id={`DVLogDetailsSummary__Field__Label--${label}`} text={label} />
      </div>
      <div className="DVLogDetailsSummary__Field__Value">{valueNode}</div>
    </div>
  );
};

type DVLogDetailsSummaryProps = {
  initialData?: Partial<Query>;
  logEntry: LogEntry;
  schema?: Schema;
};

const DVLogDetailsSummary = ({ initialData, logEntry, schema }: DVLogDetailsSummaryProps) => {
  const { categoryField, dataSource, fields, summaryFieldIds = [], summaryFieldIdsByCategory } = schema || {};
  const values = { ...logEntry.values, ...logEntry.serverInfo };

  const ActionsButtonComponent = dataSource && plugin?.actionsButtons?.[dataSource];

  const getFieldRenderer = (fieldId: string) => {
    const schemaFieldType = schema?.fields?.[fieldId]?.type;
    if (schemaFieldType) {
      return plugin.fieldRenderers?.[schemaFieldType];
    }
  };

  const renderSummaryFields = (summaryFieldIds: string[] = []) =>
    summaryFieldIds
      .map((fieldId) => {
        if (!values[fieldId]) {
          return null;
        }

        return {
          fieldId,
          label: fields?.[fieldId]?.label || '',
          value: values[fieldId],
          renderer: getFieldRenderer(fieldId),
        };
      })
      .filter(exists)
      .map((field) => <Field key={field.fieldId} schema={schema} row={values} {...field} />);

  const obtainSummaryFieldIds = useCallback(() => {
    if (summaryFieldIdsByCategory && categoryField) {
      const categoryValue = values[categoryField];
      if (categoryField) {
        return summaryFieldIdsByCategory[categoryValue];
      }
    } else if (summaryFieldIds.length > 0) {
      return summaryFieldIds;
    }

    return [];
  }, [categoryField, summaryFieldIds, summaryFieldIdsByCategory, values]);

  const resolvedSummaryFieldIds = obtainSummaryFieldIds();

  return (
    <div className="DVLogDetailsSummary">
      <div className="DVLogDetailsSummary__Header">
        <EventTypeFieldRenderer
          fieldId={'event.type'}
          row={values}
          schemaFields={schema?.fields}
          value={values['event.type']}
          size={EventTypeFieldRendererSize.LARGE}
        />
        <DataSourceFieldRenderer
          fieldId={DATA_SOURCE_NAME_FIELD}
          value={values[DATA_SOURCE_NAME_FIELD]}
          row={values}
          size={DataSourceFieldRendererSize.LARGE}
        />
      </div>

      <div className="DVLogDetailsSummary__FieldsArea">
        <div className="DVLogDetailsSummary__FieldsArea__Fields">{renderSummaryFields(resolvedSummaryFieldIds)}</div>
        {ActionsButtonComponent && (
          <div className="DVLogDetailsSummary__FieldsArea__Actions">
            <ActionsButtonComponent values={values} initialData={initialData} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DVLogDetailsSummary;
